import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  BiUpload,
  BiFolder,
  BiFolderOpen,
  BiFile,
  BiPlus,
  BiMove,
  BiChevronRight,
  BiChevronDown,
  BiHome,
} from "react-icons/bi";
import { AiOutlineEye, AiOutlineDelete } from "react-icons/ai";
import { useToast } from "../../context/ToastContext";
import { useAuth } from "../../context/AuthContext";
import DeleteConfirmationModal from "../../components/DeleteConfirmationModal/DeleteConfirmationModal";
import "./RenderMyDocs.scss";
import {
  createFile,
  createFolder,
  getDirectoryTree,
  deleteDocument,
  moveDocument,
  getMimeTypeFromFilename,
  getFileSizeFromResponse,
} from "../../services/api/AddDocApi";
// Replace Firebase import with FTP upload utility
import { uploadFileWithProgress } from "../../utils/ftpUploadUtils";
import { getAcceptString, getErrorMessage, validateFileType } from "../../utils/fileValidationUtils";

export const RenderMyDocs = ({
  mydocs,
  selectedCourtType,
  courtRef,
  refreshDocs,
}) => {
  const [expandedFolders, setExpandedFolders] = useState(new Set(["root"])); // Start with root expanded
  const [selectedItem, setSelectedItem] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createType, setCreateType] = useState("file");
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    docId: null,
    docName: "",
  });
  const [moveModal, setMoveModal] = useState({ isOpen: false, document: null });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState("");
  const [folderName, setFolderName] = useState("");
  const [currentFolderId, setCurrentFolderId] = useState(null); // null represents root

  // Add FTP upload related states
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);

  const createModalRef = useRef(null);

  const { showToast } = useToast();
  const { role, company } = useAuth();

  // Handle close modal function
  const handleCloseCreateModal = useCallback(() => {
    setShowCreateModal(false);
    setUploadedUrl("");
    setFolderName("");
    setSelectedFile(null);
    setUploading(false);
    setUploadProgress(0);
  }, []);

  // Handle click outside modal to close
  useEffect(() => {
    const handleModalClickOutside = (event) => {
      if (
        showCreateModal &&
        createModalRef.current &&
        !createModalRef.current.contains(event.target)
      ) {
        handleCloseCreateModal();
      }
    };

    if (showCreateModal) {
      document.addEventListener("mousedown", handleModalClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleModalClickOutside);
    };
  }, [showCreateModal, handleCloseCreateModal]);

  // Debug logging to see what data we're receiving
  useEffect(() => {
    console.log("=== RenderMyDocs Props Debug ===");
    console.log("mydocs:", mydocs);
    console.log("mydocs.myDocuments:", mydocs?.myDocuments);
    console.log("mydocs.total:", mydocs?.total);
    console.log("courtRef:", courtRef);
  }, [mydocs, courtRef]);

  // Use mydocs.myDocuments as the directory tree
  const directoryTree = mydocs?.myDocuments || [];

  const toggleFolder = (folderId) => {
    const newExpanded = new Set(expandedFolders);
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
    }
    setExpandedFolders(newExpanded);
  };

  const handleItemClick = (item, parentId = null) => {
    setSelectedItem(item._id);
    if (item.type === "folder") {
      // Toggle folder expansion on click
      toggleFolder(item._id);
      // Set the current folder ID to the clicked folder's ID so new items are created inside it
      setCurrentFolderId(item._id);
    } else {
      // If clicking on a file, set the current folder to its parent
      setCurrentFolderId(parentId);
    }
  };

  const handlePreview = (url) => {
    if (url) {
      window.open(url, "_blank");
    }
  };

  const handleDelete = (docId, docName = "document") => {
    setDeleteModal({ isOpen: true, docId, docName });
  };

  const confirmDelete = async () => {
    try {
      await deleteDocument(deleteModal.docId);
      showToast("Document deleted successfully!", "success");
      setDeleteModal({ isOpen: false, docId: null, docName: "" });

      // Reset currentFolderId to null (root) if we're in an empty directory
      // This will be handled by the refreshDocs callback
      if (typeof refreshDocs === "function") {
        refreshDocs();
      }
    } catch (error) {
      console.error("Error deleting document:", error);
      showToast("Failed to delete document!", "error");
    }
  };

  const cancelDelete = () => {
    setDeleteModal({ isOpen: false, docId: null, docName: "" });
  };

  const handleMove = (document) => {
    setMoveModal({ isOpen: true, document });
  };

  const handleMoveComplete = async (targetFolderId) => {
    try {
      await moveDocument(moveModal.document._id, targetFolderId);
      showToast("Document moved successfully!", "success");
      setMoveModal({ isOpen: false, document: null });
      if (typeof refreshDocs === "function") {
        refreshDocs();
      }
    } catch (error) {
      console.error("Error moving document:", error);
      showToast("Failed to move document!", "error");
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;

    // Validate file type frst
    const validation = validateFileType(file);

    if (!validation.isValid) {
      const errorMessage = getErrorMessage(
        validation.errorType,
        validation.error
      );
      showToast(errorMessage, "error");
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setSelectedFile(file);

    try {
      // Generate file path based on your requirements - similar to TaskForm
      const filePath = company?._id ? `/${company._id}/mydocs` : `/unknown`;

      await uploadFileWithProgress(
        file,
        filePath,
        // Progress callback
        (progress) => {
          setUploadProgress(progress);
        },
        // Success callback
        (result) => {
          console.log("File uploaded successfully:", result);
          setUploadedUrl(result.imageUrl); // Use the processed URL part
          setUploading(false);
          showToast("File uploaded successfully!", "success");
        },
        // Error callback
        (error) => {
          console.error("File upload failed:", error);
          setUploading(false);
          setUploadProgress(0);
          setSelectedFile(null);
          showToast(error.message || "File upload failed", "error");
        }
      );
    } catch (error) {
      console.error("File upload failed:", error);
      setUploading(false);
      setUploadProgress(0);
      setSelectedFile(null);
      showToast(error.message || "File upload failed", "error");
    }
  };

  const handleCreateFile = async () => {
    if (!uploadedUrl) {
      showToast("Please upload a document first!", "error");
      return;
    }

    if (!courtRef) {
      showToast("Court reference is required!", "error");
      return;
    }

    setIsSubmitting(true);

    try {
      const filename = selectedFile
        ? selectedFile.name
        : extractFilenameFromUrl(uploadedUrl);
      const payload = {
        name: filename,
        courtRef: courtRef,
        url: `${process.env.REACT_APP_BACKEND_MAIN_URL_PATH}ftp/view/${uploadedUrl}`, // Construct full URL
        parentId: currentFolderId,
        size: selectedFile ? selectedFile.size : 0,
        mimeType: selectedFile
          ? selectedFile.type
          : getMimeTypeFromFilename(filename),
        metadata: {
          uploadedAt: new Date().toISOString(),
        },
      };

      await createFile(payload);
      showToast("File uploaded successfully!", "success");
      handleCloseCreateModal();
      if (typeof refreshDocs === "function") {
        refreshDocs();
      }
    } catch (error) {
      console.error("Error creating file:", error);
      showToast("Failed to upload file!", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateFolder = async () => {
    if (!folderName.trim()) {
      showToast("Please enter a folder name!", "error");
      return;
    }

    if (!courtRef) {
      showToast("Court reference is required!", "error");
      return;
    }

    setIsSubmitting(true);

    try {
      const payload = {
        name: folderName.trim(),
        courtRef: courtRef,
        parentId: currentFolderId,
      };

      await createFolder(payload);
      showToast("Folder created successfully!", "success");
      handleCloseCreateModal();
      if (typeof refreshDocs === "function") {
        refreshDocs();
      }
    } catch (error) {
      console.error("Error creating folder:", error);
      showToast(error.message || "Failed to create folder!", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate the file before attempting upload
      const validation = validateFileType(file);

      if (!validation.isValid) {
        const errorMessage = getErrorMessage(
          validation.errorType,
          validation.error
        );
        showToast(errorMessage, "error");

        // Clear the input
        e.target.value = "";
        return;
      }

      // File is valid, proceed with upload
      handleFileUpload(file);
    }
  };

  const handleCancelUpload = () => {
    setSelectedFile(null);
    setUploadedUrl("");
    setUploadProgress(0);
    setUploading(false);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const formatFileSize = (bytes) => {
    if (!bytes || bytes === 0) return "0 KB";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const extractFilenameFromUrl = (url) => {
    if (!url) return "untitled";
    try {
      // For FTP URLs, extract filename from the path
      const urlParts = url.split("/");
      const lastPart = urlParts[urlParts.length - 1];
      const decodedFilename = decodeURIComponent(lastPart.split("?")[0]);

      // Remove timestamp prefix if it exists
      const cleanedFilename = decodedFilename.replace(/^\d+-/, "");

      if (cleanedFilename && cleanedFilename.length > 0) {
        return cleanedFilename;
      }

      return "Document";
    } catch (error) {
      console.error("Error extracting filename:", error);
      return "Document";
    }
  };

  const getFileIcon = (item, isExpanded = false) => {
    if (item.type === "folder") {
      return isExpanded ? (
        <BiFolderOpen className="file-icon folder-icon" size={20} />
      ) : (
        <BiFolder className="file-icon folder-icon" size={20} />
      );
    }

    const mimeType = item.mimeType || "";
    if (mimeType.includes("pdf")) {
      return <BiFile className="file-icon pdf-icon" size={20} />;
    } else if (mimeType.includes("image")) {
      return <BiFile className="file-icon image-icon" size={20} />;
    } else if (mimeType.includes("word") || mimeType.includes("document")) {
      return <BiFile className="file-icon doc-icon" size={20} />;
    } else if (mimeType.includes("sheet") || mimeType.includes("excel")) {
      return <BiFile className="file-icon excel-icon" size={20} />;
    }
    return <BiFile className="file-icon default-icon" size={20} />;
  };

  // New function to render file preview (thumbnail or icon)
  const getFilePreview = (item, isExpanded = false) => {
    if (item.type === "folder") {
      return (
        <div className="file-preview-container folder">
          {isExpanded ? (
            <BiFolderOpen className="file-icon folder-icon" size={20} />
          ) : (
            <BiFolder className="file-icon folder-icon" size={20} />
          )}
        </div>
      );
    }

    const mimeType = item.mimeType || "";

    // Show image thumbnails for image files
    if (mimeType.includes("image") && item.url) {
      return (
        <div className="file-preview-container image">
          <img
            src={item.url}
            alt={item.name}
            className="image-thumbnail"
            onError={(e) => {
              // Fallback to icon if image fails to load
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "block";
            }}
          />
          <BiFile
            className="file-icon image-icon fallback-icon"
            size={20}
            style={{ display: "none" }}
          />
        </div>
      );
    }

    // Show appropriate icons for other file types
    return (
      <div className="file-preview-container file">
        {mimeType.includes("pdf") ? (
          <BiFile className="file-icon pdf-icon" size={20} />
        ) : mimeType.includes("word") || mimeType.includes("document") ? (
          <BiFile className="file-icon doc-icon" size={20} />
        ) : mimeType.includes("sheet") || mimeType.includes("excel") ? (
          <BiFile className="file-icon excel-icon" size={20} />
        ) : (
          <BiFile className="file-icon default-icon" size={20} />
        )}
      </div>
    );
  };

  // Function to get clean display name
  const getDisplayName = (item) => {
    // For folders, use the name as-is
    if (item.type === "folder") {
      return item.name;
    }

    // For files, prioritize extracting from URL if available
    if (item.url) {
      const cleanName = extractFilenameFromUrl(item.url);
      // Use extracted name if it's meaningful
      if (cleanName && cleanName !== "Document" && cleanName !== "untitled") {
        return cleanName;
      }
    }

    // If item.name looks like a URL-encoded string, try to decode it
    if (item.name && item.name.includes("%")) {
      try {
        const decoded = decodeURIComponent(item.name);
        const filename = decoded.split("/").pop() || decoded;
        // Remove timestamp prefix if it exists
        const cleanedFilename = filename.replace(/^\d+-/, "");
        return cleanedFilename || item.name;
      } catch (error) {
        return item.name;
      }
    }

    // Return item name as fallback, removing timestamp prefix if it exists
    if (item.name) {
      return item.name.replace(/^\d+-/, "");
    }

    return "Untitled Document";
  };

  const handleDownload = async (url, filename = null) => {
    try {
      showToast("Downloading file...", "info");

      const response = await fetch(url);
      if (!response.ok) {
        throw new Error(`Failed to download: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;

      if (filename) {
        link.download = filename;
      } else {
        const urlParts = url.split("/");
        const lastPart = urlParts[urlParts.length - 1];
        const cleanFilename = lastPart.split("?")[0];
        link.download = cleanFilename || "document";
      }

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);

      showToast("File downloaded successfully!", "success");
      console.log("File downloaded:", filename || url);
    } catch (error) {
      console.error("Download error:", error);
      showToast("Failed to download file. Opening in new tab.", "error");
      window.open(url, "_blank");
    }
  };

  // Helper function to find folder by ID in the tree
  const findFolderById = (tree, folderId) => {
    for (const item of tree) {
      if (item._id === folderId) {
        return item;
      }
      if (item.children && item.children.length > 0) {
        const found = findFolderById(item.children, folderId);
        if (found) return found;
      }
    }
    return null;
  };

  // Improved function to get current folder name
  const getCurrentFolderName = () => {
    // If currentFolderId is null, we're in root
    if (!currentFolderId) {
      return "Root";
    }

    // Find the folder in the directory tree
    const folder = findFolderById(directoryTree, currentFolderId);
    return folder ? folder.name : "Unknown";
  };

  // Handle root button click
  const handleRootClick = () => {
    setCurrentFolderId(null);
    setSelectedItem(null);
  };

  // Recursive function to render the tree structure
  const renderTreeItems = (items, level = 0, parentId = null) => {
    if (!items || items.length === 0) return null;

    return items.map((item) => {
      const isExpanded = expandedFolders.has(item._id);
      const isSelected = selectedItem === item._id;
      const hasChildren = item.children && item.children.length > 0;

      return (
        <div key={item._id} className="tree-item">
          <div
            className={`tree-item-content ${isSelected ? "selected" : ""} ${
              item.type
            }`}
            style={{ paddingLeft: `${level * 20 + 8}px` }}
            onClick={() => handleItemClick(item, parentId)}
            onDoubleClick={() =>
              item.type === "file" && handlePreview(item.url)
            }
          >
            <div className="tree-item-left">
              {item.type === "folder" ? (
                <span className="expand-icon">
                  {hasChildren ? (
                    isExpanded ? (
                      <BiChevronDown size={16} />
                    ) : (
                      <BiChevronRight size={16} />
                    )
                  ) : (
                    <span
                      style={{ width: "16px", display: "inline-block" }}
                    ></span>
                  )}
                </span>
              ) : (
                <span style={{ width: "16px", display: "inline-block" }}></span>
              )}

              {getFilePreview(item, isExpanded)}

              <span className="item-name" title={getDisplayName(item)}>
                {getDisplayName(item)}
              </span>
            </div>

            <div className="tree-item-right">
              <span className="item-date">{formatDate(item.createdAt)}</span>
              <span className="item-size">
                {item.type === "folder"
                  ? `${item.children?.length || 0} items`
                  : formatFileSize(item.size)}
              </span>

              <div className="item-actions">
                {item.type === "file" && (
                  <button
                    className="action-btn download-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDownload(item.url, item.name);
                    }}
                    title="Download"
                  >
                    <BiUpload
                      size={16}
                      style={{ transform: "rotate(180deg)" }}
                    />
                  </button>
                )}

                <button
                  className="action-btn delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(item._id, item.name);
                  }}
                  title="Delete"
                >
                  <AiOutlineDelete size={16} />
                </button>
              </div>
            </div>
          </div>

          {/* Render children if folder is expanded */}
          {item.type === "folder" && isExpanded && hasChildren && (
            <div className="tree-children">
              {renderTreeItems(item.children, level + 1, item._id)}
            </div>
          )}
        </div>
      );
    });
  };

  // Check if we have any data to display
  if (!mydocs || !mydocs.myDocuments) {
    return (
      <div className="my-docs-container loading">Loading documents...</div>
    );
  }

  return (
    <div className="my-docs-container hierarchy">
      {/* Header */}
      <div className="my-docs-header">
        <div className="header-top">
          <h3>My Documents</h3>
          <div className="header-actions">
            <button
              className="add-doc-btn"
              onClick={() => {
                setCreateType("folder");
                setShowCreateModal(true);
              }}
            >
              <BiPlus /> New Folder
            </button>
            <button
              className="add-doc-btn primary"
              onClick={() => {
                setCreateType("file");
                setShowCreateModal(true);
              }}
            >
              <BiPlus /> Add Document
            </button>
          </div>
        </div>

        {/* Info bar with breadcrumb */}
        <div className="info-bar">
          <div className="breadcrumb">
            <button
              className={`breadcrumb-item ${!currentFolderId ? "active" : ""}`}
              onClick={handleRootClick}
            >
              <BiHome size={16} />
              Root
            </button>
            {currentFolderId && (
              <>
                <BiChevronRight size={14} />
                <span className="breadcrumb-item active">
                  {getCurrentFolderName()}
                </span>
              </>
            )}
          </div>
          <span className="total-items">{mydocs.total || 0} items total</span>
        </div>
      </div>

      {/* Tree View */}
      <div className={directoryTree.length === 0 ? "" : "documents-tree"}>
        {directoryTree.length === 0 ? (
          <div className="no-data-container">
            <h3 className="no-data-title">No Documents Found</h3>
            <p className="no-data-message">
              You haven't uploaded any documents yet. Click "Add Document" or
              "New Folder" to get started.
            </p>
          </div>
        ) : (
          <div className="tree-container">
            <div className="tree-header">
              <div className="tree-column name-column">Name</div>
              <div className="tree-column date-column">Date Modified</div>
              <div className="tree-column size-column">Size</div>
              <div className="tree-column actions-column">Actions</div>
            </div>
            <div className="tree-content">{renderTreeItems(directoryTree)}</div>
          </div>
        )}
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="modal-backdrop">
          <div className="modal" ref={createModalRef}>
            <div className="modal-header">
              <h3>
                {createType === "folder"
                  ? "Create New Folder"
                  : "Add New Document"}
              </h3>
              <button className="close-btn" onClick={handleCloseCreateModal}>
                ×
              </button>
            </div>

            <div className="add-doc-form">
              <div className="form-group">
                <label>Court Reference</label>
                <input
                  type="text"
                  value={courtRef || ""}
                  readOnly
                  className="court-ref-input"
                />
              </div>

              <div className="form-group">
                <label>Create in</label>
                <div className="location-input-container">
                  <BiHome size={16} />
                  <input
                    type="text"
                    value={getCurrentFolderName()}
                    readOnly
                    className="location-input"
                  />
                  {currentFolderId && (
                    <button
                      type="button"
                      onClick={handleRootClick}
                      className="root-btn"
                      title="Move to Root"
                    >
                      <BiHome size={14} />
                    </button>
                  )}
                </div>
              </div>

              {createType === "folder" ? (
                <div className="form-group">
                  <label>Folder Name</label>
                  <input
                    type="text"
                    value={folderName}
                    onChange={(e) => setFolderName(e.target.value)}
                    placeholder="Enter folder name"
                    className="folder-name-input"
                  />
                </div>
              ) : (
                <div className="form-group">
                  <label>Upload Document</label>
                  <div className="upload-section">
                    <input
                      type="file"
                      onChange={handleFileChange}
                      className="file-input"
                      disabled={uploading}
                      accept={getAcceptString()}
                      title="Allowed formats: PDF, DOC, DOCX, TXT, PNG, JPEG, JPG"
                    />
                    <div className="file-format-info">
                      <small>
                        Allowed formats: PDF, DOC, DOCX, TXT, PNG, JPEG, JPG
                        (Max 50MB)
                      </small>
                    </div>
                    {uploading && (
                      <div className="upload-progress">
                        <div className="progress-bar">
                          <div
                            className="progress"
                            style={{ width: `${uploadProgress}%` }}
                          />
                        </div>
                        <span>{Math.round(uploadProgress)}% uploaded</span>
                      </div>
                    )}

                    {uploadedUrl && selectedFile && (
                      <div className="uploaded-file-preview">
                        <div className="file-info">
                          <BiFile size={16} />
                          <span>{selectedFile.name}</span>
                          <button
                            type="button"
                            onClick={handleCancelUpload}
                            className="cancel-upload-button"
                          >
                            ×
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="modal-actions">
                <button
                  type="button"
                  className="cancel-btn"
                  onClick={handleCloseCreateModal}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="submit-btn"
                  disabled={
                    isSubmitting ||
                    uploading ||
                    (createType === "file" && !uploadedUrl) ||
                    (createType === "folder" && !folderName.trim())
                  }
                  onClick={
                    createType === "folder"
                      ? handleCreateFolder
                      : handleCreateFile
                  }
                >
                  {isSubmitting
                    ? createType === "folder"
                      ? "Creating..."
                      : "Adding..."
                    : uploading
                    ? "Uploading..."
                    : createType === "folder"
                    ? "Create Folder"
                    : "Add Document"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmationModal
        isOpen={deleteModal.isOpen}
        itemType={deleteModal.docName}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </div>
  );
};
